import React, { Fragment } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// Components

import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { SSAP_MEDICAL_EMERGENCY_PROPS } from '@constants/ssap/miscellaneous/miscellaneous';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';


/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Member Enrolled In Other Health Insurance
 *
 */
const Page4_MedicalEmergency = props => {

    const {
        currentHhm,
        data,
        formName,
        hhmIndex,
        householdMembers,
        environment: {
            localDates: { serverDate },
        }
    } = props;

    const {
        medicalEmergency: {
            needsEMA,
            dateEMABegan,
            hasEMAEnded,
            dateEMAEnded
        },
        name
    } = currentHhm;

    const onFieldChange = (newData, _, __, [name]) => {
        const newState = { ...data };
        let medicalEmergency = newData.medicalEmergency ?? {};

        switch (name) {
            case SSAP_MEDICAL_EMERGENCY_PROPS.NEEDS_EMA:
                medicalEmergency[SSAP_MEDICAL_EMERGENCY_PROPS.DATE_EMA_BEGAN] = null;
                medicalEmergency[SSAP_MEDICAL_EMERGENCY_PROPS.HAS_EMA_ENDED] = null;
                // fall through

            case SSAP_MEDICAL_EMERGENCY_PROPS.HAS_EMA_ENDED:
                medicalEmergency[SSAP_MEDICAL_EMERGENCY_PROPS.DATE_EMA_ENDED] = null;
        }

        newData.medicalEmergency = medicalEmergency;
        newState.taxHousehold.householdMember[hhmIndex] = { ...newData };
        props.actions.changeData(newState);
    };

    return (
        <Fragment>
            <Form {...{ formName }}>
                <div
                    className='fade-in'
                    id='aiStateHealthBenefit'
                >
                    <div className='subsection' >
                        <UniversalInput
                            legend={{
                                legendText: 'ssap.input.legend.needsEMA',
                                legendTextReplacements: {
                                    personName: (
                                        <strong> {normalizeNameOfPerson(name)} </strong>
                                    ),
                                }
                            }}
                            label={{
                                showRequiredIcon: true
                            }}
                            inputData={{
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'needsEMA',
                                value: needsEMA,
                                options: DROPDOWN_OPTIONS.YES_NO,
                                validationType: VALIDATION_TYPES.CHOSEN,
                                currentData: householdMembers[hhmIndex],
                                fields: ['medicalEmergency'],
                                isInputStandalone: true

                            }}
                            inputActions={{
                                onFieldChange
                            }}
                            errorClasses={['margin-l-0']}
                        />

                        <ConditionalRendering isTrue={needsEMA}>
                            <UniversalInput
                                label={{
                                    text: 'ssap.input.label.dateEMABegan',
                                    labelClass: ['padding-0'],
                                }}
                                containerClass={['mt-0 pb-3']}
                                inputData={{
                                    isInputRequired: true,
                                    type: INPUT_TYPES.DATE_PICKER,
                                    name: 'dateEMABegan',
                                    value: dateEMABegan,
                                    valuesToMatch: [serverDate, 90],
                                    validationType: VALIDATION_TYPES.LAST_N_DAYS,
                                    validationErrorMessage: VALIDATION_ERROR_MESSAGES.DATE_WITHIN_3_MONTHS,
                                    currentData: householdMembers[hhmIndex],
                                    fields: ['medicalEmergency'],
                                    isInputStandalone: true
                                }}
                                errorClasses={['margin-l-0']}
                                inputActions={{
                                    onFieldChange
                                }}
                            />

                            <UniversalInput
                                legend={{
                                    legendText: 'ssap.input.legend.hasEMAEnded',
                                }}
                                label={{
                                    showRequiredIcon: true
                                }}
                                inputData={{
                                    isInputRequired: true,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'hasEMAEnded',
                                    value: hasEMAEnded,
                                    options: DROPDOWN_OPTIONS.YES_NO,
                                    validationType: VALIDATION_TYPES.CHOSEN,
                                    currentData: householdMembers[hhmIndex],
                                    fields: ['medicalEmergency'],
                                    isInputStandalone: true

                                }}
                                inputActions={{
                                    onFieldChange
                                }}
                                errorClasses={['margin-l-0']}
                            />
                        </ConditionalRendering>

                        <ConditionalRendering isTrue={dateEMABegan && hasEMAEnded}>
                            <UniversalInput
                                label={{
                                    text: 'ssap.input.label.dateEMAEnded',
                                    labelClass: ['padding-0'],
                                }}
                                containerClass={['mt-0 pb-3']}
                                inputData={{
                                    isInputRequired: true,
                                    type: INPUT_TYPES.DATE_PICKER,
                                    name: 'dateEMAEnded',
                                    helperClasses: [],
                                    value: dateEMAEnded,
                                    valuesToMatch: [dateEMABegan, serverDate],
                                    validationType: VALIDATION_TYPES.DATE_BETWEEN_TWO_DATES,
                                    validationErrorMessage: VALIDATION_ERROR_MESSAGES.DATE_VALID_END_DATE,
                                    currentData: householdMembers[hhmIndex],
                                    fields: ['medicalEmergency'],
                                    isInputStandalone: true
                                }}
                                errorClasses={['margin-l-0']}
                                inputActions={{
                                    onFieldChange
                                }}
                            />
                        </ConditionalRendering>

                    </div>
                </div>
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    data: state.ssap.data,
    environment: state.common.environment,
    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page4_MedicalEmergency);
